import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import {
    IconButton,
    Badge,
    Switch
} from '@mui/material';
import {
    Notifications,
    NotificationsNone,
    CheckCircle,
    Error,
    Warning,
    Info
} from '@mui/icons-material';
import { useNotification } from '../context/NotificationContext';
import { useAlerts } from '../context/AlertContext';
import { useAuth } from '../context/useAuth';
import { useTimezoneFormatter } from '../hooks/useTimezoneFormatter';
import { formatRelativeTime } from '../utils/dateTime';
import { isPlatformScoped } from '../utils/permissions';
import Drawer from './core/feedback/Drawer';

const AlertNotificationCenter = ({ isSidebarOpen = false }) => {
    const navigate = useNavigate();
    const { user } = useAuth();
    const { formatDate } = useTimezoneFormatter();
    const isPlatformAdmin = isPlatformScoped(user) && user?.roleId !== 'r3';

    const { addNotification } = useNotification();
    const {
        alerts,
        unreadCount,
        loading,
        fetchAlerts,
        markAsRead,
        markAllRead,
        clearAllAlerts,
        deleteAlert,
        showTenantAlerts,
        toggleTenantAlerts
    } = useAlerts();

    const [isOpen, setIsOpen] = useState(false);

    const handleMarkAsRead = async (alertId) => {
        await markAsRead(alertId);
    };

    const handleMarkAllRead = async () => {
        await markAllRead();
        addNotification('All alerts marked as read', 'success');
    };

    const handleDeleteAlert = async (alertId) => {
        await deleteAlert(alertId);
        addNotification('Alert deleted', 'success');
    };

    const getAlertIcon = (type) => {
        switch (type) {
            case 'success': return <CheckCircle className="w-5 h-5 shrink-0 text-emerald-500 mt-0.5" />;
            case 'error': return <Error className="w-5 h-5 shrink-0 text-red-500 mt-0.5" />;
            case 'warning': return <Warning className="w-5 h-5 shrink-0 text-amber-500 mt-0.5" />;
            case 'info':
            default: return <Info className="w-5 h-5 shrink-0 text-blue-500 mt-0.5" />;
        }
    };

    const getAlertBorderColor = (type) => {
        switch (type) {
            case 'success': return 'border-l-emerald-500';
            case 'error': return 'border-l-red-500';
            case 'warning': return 'border-l-amber-500';
            case 'info':
            default: return 'border-l-blue-500';
        }
    };

    return (
        <div className="text-left">
            <IconButton
                onClick={() => {
                    const next = !isOpen;
                    setIsOpen(next);
                    if (next) {
                        console.log('[AlertNotificationCenter] Opening — silent refresh');
                        fetchAlerts(true); // silent=true: no loading spinner flash
                    }
                }}
                size="large"
                className={`text-gray-500 dark:text-gray-400 hover:bg-black/5 dark:hover:bg-white/5 transition-all duration-200 ${isOpen ? 'bg-black/5 dark:bg-white/5 text-gray-700 dark:text-gray-200' : ''}`}
            >
                <Badge badgeContent={unreadCount} color="error">
                    {unreadCount > 0 ? <Notifications /> : <NotificationsNone />}
                </Badge>
            </IconButton>

            <Drawer
                open={isOpen}
                onClose={() => setIsOpen(false)}
                anchor="right"
                size="sm"
                title={
                    <div className="flex items-center gap-2">
                        <Notifications className="text-[var(--primary-color)]" sx={{ fontSize: 20 }} />
                        <span className="text-[15px] font-bold text-gray-900 dark:text-white leading-tight">
                            Notifications
                        </span>
                    </div>
                }
                className="bg-white dark:bg-gray-900 border-l border-gray-200/60 dark:border-gray-800 shadow-2xl rounded-l-[var(--border-radius,12px)] overflow-hidden font-sans"
            >
                <div className="flex flex-col h-full">
                    {/* Sticky Action Toolbar */}
                    <div className="flex items-center justify-between pb-3 border-b border-gray-100 dark:border-gray-800 mb-4 flex-shrink-0">
                        {isPlatformAdmin ? (
                            <div className="flex items-center gap-1.5 select-none shrink-0">
                                <Switch
                                    size="small"
                                    checked={showTenantAlerts}
                                    onChange={toggleTenantAlerts}
                                    sx={{
                                        '& .MuiSwitch-switchBase.Mui-checked': {
                                            color: 'var(--primary-color)',
                                        },
                                        '& .MuiSwitch-switchBase.Mui-checked + .MuiSwitch-track': {
                                            backgroundColor: 'var(--primary-color)',
                                        },
                                    }}
                                />
                                <span className="text-[9px] text-gray-400 font-bold uppercase tracking-wider">Tenant Alerts</span>
                            </div>
                        ) : (
                            <span className="text-xs text-gray-400 dark:text-gray-500 font-medium">
                                {unreadCount > 0 ? `${unreadCount} unread` : 'All caught up'}
                            </span>
                        )}

                        <div className="flex items-center gap-1 shrink-0">
                            {unreadCount > 0 && (
                                <button 
                                    onClick={handleMarkAllRead}
                                    className="text-[11px] font-bold hover:opacity-85 px-2 py-1 rounded hover:bg-black/5 dark:hover:bg-white/5 transition-all"
                                    style={{ color: 'var(--primary-color)' }}
                                >
                                    Mark all read
                                </button>
                            )}
                            {alerts.length > 0 && (
                                <button 
                                    onClick={clearAllAlerts}
                                    className="text-[11px] font-bold text-red-500 hover:text-red-600 px-2 py-1 rounded hover:bg-red-50 dark:hover:bg-red-950/20 transition-all"
                                >
                                    Clear
                                </button>
                            )}
                        </div>
                    </div>

                    {/* Alert List Container */}
                    <div className="flex-1 overflow-y-auto scrollbar-thin scrollbar-thumb-gray-200 dark:scrollbar-thumb-gray-800 flex flex-col pr-1">
                        {loading && (
                            <div className="p-8 text-center text-gray-400 dark:text-gray-500 text-[13px]">
                                Loading...
                            </div>
                        )}

                        {!loading && alerts.length === 0 && (
                            <div className="flex flex-col items-center justify-center p-8 text-center flex-1">
                                <NotificationsNone className="text-gray-300 dark:text-gray-700 mb-2 shrink-0" sx={{ fontSize: 44 }} />
                                <span className="text-[13px] font-medium text-gray-400 dark:text-gray-500">No notifications</span>
                            </div>
                        )}

                        {!loading && alerts.map((alert) => {
                            const borderCol = getAlertBorderColor(alert.alert_type);
                            return (
                                <div
                                    key={alert.id}
                                    onClick={() => {
                                        if (!alert.is_read) handleMarkAsRead(alert.id);
                                        if (alert.alert_metadata?.action_url) {
                                            setIsOpen(false);
                                            navigate(alert.alert_metadata.action_url);
                                        }
                                    }}
                                    className={`flex items-start gap-3 p-4 mb-3 border-l-4 border border-gray-100/80 dark:border-gray-800/80 cursor-pointer shadow-sm transition-all duration-200 hover:shadow-md ${borderCol} ${
                                        alert.is_read 
                                            ? 'bg-white dark:bg-gray-900 hover:bg-gray-50/50 dark:hover:bg-gray-800/50' 
                                            : 'bg-primary/[0.02] dark:bg-primary/[0.04] hover:bg-primary/[0.05] dark:hover:bg-primary/[0.08] border-primary/20 dark:border-primary/30'
                                    }`}
                                    style={{ borderRadius: 'var(--border-radius, 8px)' }}
                                >
                                    {getAlertIcon(alert.alert_type)}
                                    <div className="flex-1 min-w-0">
                                        <div className="flex items-start justify-between gap-2 mb-1">
                                            <span className={`text-[12.5px] break-words text-gray-800 dark:text-gray-200 leading-snug ${alert.is_read ? 'font-medium' : 'font-bold text-gray-900 dark:text-white'}`}>
                                                {alert.title}
                                            </span>
                                            {!alert.is_read && (
                                                <span className="shrink-0 text-[9px] font-bold px-1.5 py-0.5 rounded uppercase tracking-wider" style={{ color: 'var(--primary-color)', backgroundColor: 'rgba(var(--primary-rgb), 0.12)' }}>
                                                    New
                                                </span>
                                            )}
                                        </div>

                                        {alert.tenant_name && (
                                            <span className="inline-block mt-0.5 mb-1 px-1.5 py-0.5 text-[9px] font-semibold text-blue-500 dark:text-blue-400 bg-blue-500/10 rounded border border-blue-500/20" style={{ borderRadius: 'var(--border-radius, 8px)' }}>
                                                {alert.tenant_name}
                                            </span>
                                        )}

                                        <p className="text-[11.5px] text-gray-500 dark:text-gray-400 leading-normal break-words mt-1">
                                            {alert.message}
                                        </p>

                                        {alert.cta && (
                                            <button 
                                                onClick={(e) => {
                                                    e.stopPropagation();
                                                    if (!alert.is_read) handleMarkAsRead(alert.id);
                                                    setIsOpen(false);
                                                    navigate(alert.cta.link, { state: { forceTab: 'password' } });
                                                }}
                                                className="mt-2 text-[11px] font-semibold text-white bg-primary hover:bg-primary/90 px-2.5 py-1 transition-all shadow-sm"
                                                style={{ borderRadius: 'var(--border-radius, 8px)', backgroundColor: 'var(--primary-color)' }}
                                            >
                                                {alert.cta.label}
                                            </button>
                                        )}

                                        <div className="flex items-center justify-between mt-2.5">
                                            <span
                                                title={formatDate(alert.created_at)}
                                                className="text-[10px] text-gray-400 dark:text-gray-500 cursor-help select-none"
                                            >
                                                {formatRelativeTime(alert.created_at)}
                                            </span>
                                            <button
                                                onClick={(e) => {
                                                    e.stopPropagation();
                                                    handleDeleteAlert(alert.id);
                                                }}
                                                className="text-[10.5px] font-semibold text-red-400 hover:text-red-500 hover:underline px-1 py-0.5 transition-all"
                                            >
                                                Delete
                                            </button>
                                        </div>
                                    </div>
                                </div>
                            );
                        })}
                    </div>
                </div>
            </Drawer>
        </div>
    );
};

export default AlertNotificationCenter;

